import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { PasswordInput } from "@/components/ui/password-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  ShieldAlert, Database, FileText, Building2, Plus, Trash2, Star, StarOff, Percent, MapPin, Pencil,
  Radio, PlugZap, RefreshCw, CheckCircle2, XCircle, Clock, Webhook, Copy, Shuffle, Send, Globe,
  Users, ArrowUp, ArrowDown, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";
import AutoProcurementSendSettings from "@/components/AutoProcurementSendSettings";
import DispatchNotificationSettings from "@/components/DispatchNotificationSettings";
import ReleaseAllStockSettings from "@/components/ReleaseAllStockSettings";

export default function SettingsPage() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agentStatus, setAgentStatus] = useState(null);
  const [agentQueue, setAgentQueue] = useState([]);
  const [agentToken, setAgentToken] = useState("");
  const [tallyLedgers, setTallyLedgers] = useState([]);
  const [terms, setTerms] = useState([]);
  const [banks, setBanks] = useState([]);
  const [newTerm, setNewTerm] = useState({ name: "", body: "", is_default: false, is_active: true });
  const [newBank, setNewBank] = useState({ name: "", account_name: "", account_number: "", ifsc: "", branch: "", is_default: false });
  const [company, setCompany] = useState(null);
  const [gstRates, setGstRates] = useState([]);
  const [newGst, setNewGst] = useState({ name: "", gst_pct: 18, status: "active", is_default: false });
  const [logoPreviewSrc, setLogoPreviewSrc] = useState("");
  const [logoPreviewNonce, setLogoPreviewNonce] = useState(0);
  const [imCfg, setImCfg] = useState(null);
  const [imCrmKeyInput, setImCrmKeyInput] = useState("");
  const [imBusy, setImBusy] = useState(false);
  const [imTesting, setImTesting] = useState(false);
  const [imSyncing, setImSyncing] = useState(false);
  const [imUsers, setImUsers] = useState([]);
  const [imSyncLogs, setImSyncLogs] = useState([]);
  // Webhook URL and Secret Key are two views of the same value and must
  // always agree — see webhookBaseFromUrl/extractKeyFromUrl below. Draft
  // state is separate from the last-saved-from-server value so "Unsaved
  // changes" / Reset have something to compare against and revert to.
  const [imWebhookUrlDraft, setImWebhookUrlDraft] = useState("");
  const [imWebhookKeyDraft, setImWebhookKeyDraft] = useState("");
  const [imWebhookSavedUrl, setImWebhookSavedUrl] = useState("");
  const [imWebhookSavedKey, setImWebhookSavedKey] = useState("");
  const [imWebhookBusy, setImWebhookBusy] = useState(false);
  const [imSendingTest, setImSendingTest] = useState(false);
  const [imDetectingIp, setImDetectingIp] = useState(false);
  const [imExpandedLogId, setImExpandedLogId] = useState(null);

  // --- Lead Assignment Rules ---
  const [laRules, setLaRules] = useState([]);
  const [laFallback, setLaFallback] = useState("");
  const [laFallbackBusy, setLaFallbackBusy] = useState(false);
  const [laBackfillBusy, setLaBackfillBusy] = useState(false);
  const [laDialogOpen, setLaDialogOpen] = useState(false);
  const [laEditingRule, setLaEditingRule] = useState(null);
  const [laSaving, setLaSaving] = useState(false);
  const LA_EMPTY_FORM = { name: "", rule_type: "region", states: "", category_name: "", keywords: "", assignee1: "__none__", assignee2: "__none__", strategy: "single", active: true };
  const [laForm, setLaForm] = useState(LA_EMPTY_FORM);
  const [laTestInput, setLaTestInput] = useState({ state: "", city: "", pincode: "", country_iso: "IN", product_text: "" });
  const [laTesting, setLaTesting] = useState(false);
  const [laTestResult, setLaTestResult] = useState(null);

  const userLabel = (email) => {
    const u = imUsers.find((x) => x.email === email);
    return u ? (u.name || u.email) : (email || "—");
  };

  const isAdmin = user?.role === "admin";

  // Re-derive the base (everything before "?key=") from whatever URL is
  // currently on screen, so editing the Secret only ever replaces the key
  // portion — the URL can never end up carrying a second URL as its key.
  const webhookBaseFromUrl = (url) => {
    const m = (url || "").match(/^(.*\/api\/webhooks\/indiamart)(\?|$)/);
    if (m) return m[1];
    return `${(process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "")}/api/webhooks/indiamart`;
  };
  const extractKeyFromUrl = (urlStr) => {
    const q = (urlStr || "").split("?")[1];
    if (!q) return "";
    try { return new URLSearchParams(q).get("key") || ""; } catch { return ""; }
  };
  const WEBHOOK_KEY_RE = /^[A-Za-z0-9_-]+$/;
  const isValidWebhookKey = (s) => !!s && s.length >= 16 && WEBHOOK_KEY_RE.test(s);
  const webhookKeyError = "Secret must be at least 16 characters — letters, digits, '-' and '_' only (no URLs, slashes, '?', '&' or spaces).";

  const seedWebhookDrafts = (cfgData) => {
    const url = cfgData?.webhook_url || `${webhookBaseFromUrl("")}?key=`;
    const key = extractKeyFromUrl(url);
    setImWebhookUrlDraft(url);
    setImWebhookKeyDraft(key);
    setImWebhookSavedUrl(cfgData?.webhook_url || "");
    setImWebhookSavedKey(key);
  };

  const onWebhookUrlChange = (e) => {
    const val = e.target.value;
    setImWebhookUrlDraft(val);
    const extracted = extractKeyFromUrl(val);
    if (extracted) setImWebhookKeyDraft(extracted); // stays in sync; a URL with no parseable key= leaves the Secret field as-is
  };

  const onWebhookKeyChange = (e) => {
    const raw = e.target.value;
    // Paste-the-whole-URL-into-Secret-Key protection, mirrored client-side.
    const candidate = raw.trim().toLowerCase().startsWith("http") ? (extractKeyFromUrl(raw) || raw) : raw;
    setImWebhookKeyDraft(candidate);
    setImWebhookUrlDraft(`${webhookBaseFromUrl(imWebhookUrlDraft)}?key=${candidate}`);
  };

  const resetWebhookDraft = () => {
    setImWebhookUrlDraft(imWebhookSavedUrl || `${webhookBaseFromUrl("")}?key=`);
    setImWebhookKeyDraft(imWebhookSavedKey);
  };

  const webhookDirty = imWebhookKeyDraft !== imWebhookSavedKey;

  const loadIndiamart = async ({ seedWebhook = false } = {}) => {
    try {
      const [cfgRes, usersRes, logsRes] = await Promise.all([
        api.get("/settings/indiamart"),
        api.get("/users"),
        api.get("/indiamart/sync-logs", { params: { limit: 10 } }),
      ]);
      setImCfg(cfgRes.data);
      // Only ever (re)seed the webhook draft on the initial load or right
      // after its own Save — not on every unrelated refresh (Test
      // Connection / Sync Now / Send Test Lead also call loadIndiamart()),
      // or an in-progress, unsaved webhook edit would get silently wiped.
      if (seedWebhook) seedWebhookDrafts(cfgRes.data);
      setImUsers(usersRes.data || []);
      setImSyncLogs(logsRes.data || []);
    } catch { /* not configured yet / not admin */ }
  };

  const loadLeadAssignment = async () => {
    try {
      const [rulesRes, settingsRes] = await Promise.all([
        api.get("/lead-assignment/rules"),
        api.get("/lead-assignment/settings"),
      ]);
      setLaRules(rulesRes.data || []);
      setLaFallback(settingsRes.data?.fallback_owner_email || "");
    } catch { /* not admin / not configured yet */ }
  };

  const loadTermsBanks = async () => {
    try {
      const [t, b] = await Promise.all([api.get("/terms"), api.get("/banks")]);
      setTerms(t.data || []);
      setBanks(b.data || []);
    } catch { /* ignore */ }
  };

  const loadTallyAgent = async () => {
    try { setTallyLedgers((await api.get("/tally-sync/account-ledgers")).data.ledgers || []); } catch { /* ignore */ }
    try {
      const [st, q] = await Promise.all([api.get("/tally-agent/status"), api.get("/tally-agent/queue", { params: { limit: 20 } })]);
      setAgentStatus(st.data);
      setAgentQueue(q.data || []);
    } catch { /* ignore */ }
  };

  const generateAgentToken = async () => {
    if (agentStatus?.token_configured && !window.confirm("Generate a new agent token? The agent on the Tally PC stops working until you paste the new token into its tally_agent.ini.")) return;
    try {
      const { data } = await api.post("/tally-agent/token");
      setAgentToken(data.token);
      await loadTallyAgent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not generate token"); }
  };

  const retryAgentJob = async (id) => {
    try {
      await api.post(`/tally-agent/queue/${id}/retry`);
      toast.success("Queued again");
      await loadTallyAgent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Retry failed"); }
  };

  const loadGstAndCompany = async () => {
    try {
      const [g, c] = await Promise.all([api.get("/gst-rates"), api.get("/settings/company")]);
      setGstRates(g.data || []);
      setCompany(c.data || {});
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/tally");
        setCfg(data);
      } catch { /* default */ setCfg({ url: "http://tallyserver:9000", company_name: "Canares Group of Company", enabled: false, mode: "direct", voucher_type: "Delivery Note", so_voucher_type: "Sales Order" }); }
      await loadTallyAgent();
      await loadTermsBanks();
      await loadGstAndCompany();
      await loadIndiamart({ seedWebhook: true });
      await loadLeadAssignment();
    })();
  }, []);

  // Fetch uploaded logo as a blob URL so <img> can render it (auth header needed).
  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    (async () => {
      if (!company?.logo_storage_path) { setLogoPreviewSrc(""); return; }
      try {
        const res = await api.get("/settings/company/logo", { responseType: "blob" });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setLogoPreviewSrc(objectUrl);
      } catch { if (!cancelled) setLogoPreviewSrc(""); }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [company?.logo_storage_path, logoPreviewNonce]);

  const saveCompany = async () => {
    setBusy(true);
    try {
      await api.put("/settings/company", company);
      toast.success("Company profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const addGst = async () => {
    if (busy) return;
    if (!newGst.name || newGst.gst_pct == null) return toast.error("Name and GST % required");
    setBusy(true);
    try {
      await api.post("/gst-rates", newGst);
      setNewGst({ name: "", gst_pct: 18, status: "active", is_default: false });
      toast.success("GST rate added");
      await loadGstAndCompany();
      window.dispatchEvent(new CustomEvent("gst-rates-updated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const setDefaultGst = async (id) => {
    try {
      await api.patch(`/gst-rates/${id}`, { is_default: true });
      toast.success("Default GST rate updated");
      await loadGstAndCompany();
      window.dispatchEvent(new CustomEvent("gst-rates-updated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const rmGst = async (id) => {
    if (!window.confirm("Delete this GST rate?")) return;
    try {
      await api.delete(`/gst-rates/${id}`);
      await loadGstAndCompany();
      window.dispatchEvent(new CustomEvent("gst-rates-updated"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addTerm = async () => {
    if (busy) return;
    if (!newTerm.name || !newTerm.body) return toast.error("Title and body required");
    setBusy(true);
    try {
      await api.post("/terms", newTerm);
      setNewTerm({ name: "", body: "", is_default: false, is_active: true });
      toast.success("Terms added");
      await loadTermsBanks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const rmTerm = async (id) => {
    if (!window.confirm("Delete this terms template?")) return;
    try { await api.delete(`/terms/${id}`); await loadTermsBanks(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Phase 34.3 — inline edit for existing T&C rows
  const [editingTerm, setEditingTerm] = useState(null); // { id, name, body, is_default, is_active }
  const startEditTerm = (t) => setEditingTerm({
    id: t.id,
    name: t.name || "",
    body: t.body || "",
    is_default: !!t.is_default,
    // Templates saved before the Active toggle existed have no is_active
    // field at all — treat that as active, same as the backend default.
    is_active: t.is_active !== false,
  });
  const cancelEditTerm = () => setEditingTerm(null);
  const saveEditTerm = async () => {
    if (busy) return;
    if (!editingTerm?.id) return;
    if (!editingTerm.name || !editingTerm.body) return toast.error("Title and body required");
    setBusy(true);
    try {
      await api.put(`/terms/${editingTerm.id}`, {
        name: editingTerm.name,
        body: editingTerm.body,
        is_default: editingTerm.is_default,
        is_active: editingTerm.is_active,
      });
      toast.success("Terms updated");
      setEditingTerm(null);
      await loadTermsBanks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  const addBank = async () => {
    if (busy) return;
    if (!newBank.name || !newBank.account_number || !newBank.ifsc || !newBank.account_name) return toast.error("Name, A/c name, A/c no, IFSC required");
    setBusy(true);
    try {
      await api.post("/banks", newBank);
      setNewBank({ name: "", account_name: "", account_number: "", ifsc: "", branch: "", is_default: false });
      toast.success("Bank added");
      await loadTermsBanks();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const rmBank = async (id) => {
    if (!window.confirm("Delete this bank account?")) return;
    try { await api.delete(`/banks/${id}`); await loadTermsBanks(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveIndiamart = async () => {
    if (imBusy || !imCfg) return;
    setImBusy(true);
    try {
      const payload = {
        api_base_url: imCfg.api_base_url,
        auto_sync: imCfg.auto_sync,
        sync_interval_minutes: imCfg.sync_interval_minutes,
        default_assignee: imCfg.default_assignee,
        backfill_days: imCfg.backfill_days,
        backfill_creates_tasks: imCfg.backfill_creates_tasks,
      };
      if (imCrmKeyInput.trim()) payload.crm_key = imCrmKeyInput.trim();
      const { data } = await api.put("/settings/indiamart", payload);
      setImCfg(data);
      setImCrmKeyInput("");
      toast.success("IndiaMART settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setImBusy(false); }
  };

  const testIndiamart = async () => {
    if (imTesting) return;
    setImTesting(true);
    try {
      const { data } = await api.post("/indiamart/test");
      toast.success(data.message || "Connection successful");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Connection failed"); }
    finally { setImTesting(false); await loadIndiamart(); }
  };

  const syncIndiamartNow = async () => {
    if (imSyncing) return;
    setImSyncing(true);
    try {
      const { data } = await api.post("/indiamart/sync");
      toast.success(`Sync complete — ${data.new} new, ${data.duplicate} duplicate (of ${data.fetched} fetched)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
    finally { setImSyncing(false); await loadIndiamart(); }
  };

  const detectOutboundIp = async () => {
    if (imDetectingIp) return;
    setImDetectingIp(true);
    try {
      const { data } = await api.get("/indiamart/outbound-ip");
      if (data.ok) {
        setImCfg((c) => (c ? { ...c, outbound_ip: data.ip, outbound_ip_checked_at: data.checked_at } : c));
        toast.success(`Outbound IP: ${data.ip}`);
      } else {
        toast.error(data.hint || data.error || "Could not detect the outbound IP");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not detect the outbound IP"); }
    finally { setImDetectingIp(false); }
  };

  const openNewRuleDialog = () => {
    setLaEditingRule(null);
    setLaForm(LA_EMPTY_FORM);
    setLaDialogOpen(true);
  };

  const openEditRuleDialog = (rule) => {
    const isRegion = rule.rule_type === "region";
    const firstCategory = (rule.match_values?.categories || [])[0] || {};
    setLaEditingRule(rule);
    setLaForm({
      name: rule.name || "",
      rule_type: rule.rule_type,
      states: isRegion ? (rule.match_values?.states || []).join(", ") : "",
      category_name: !isRegion ? (firstCategory.name || "") : "",
      keywords: !isRegion ? (firstCategory.keywords || []).join(", ") : "",
      assignee1: rule.assignee_emails?.[0] || "__none__",
      assignee2: rule.assignee_emails?.[1] || "__none__",
      strategy: rule.strategy || "single",
      active: rule.active !== false,
    });
    setLaDialogOpen(true);
  };

  const saveRule = async () => {
    if (laSaving) return;
    if (!laForm.name.trim()) return toast.error("Rule name is required");
    const assignee1 = laForm.assignee1 === "__none__" ? "" : laForm.assignee1;
    const assignee2 = laForm.assignee2 === "__none__" ? "" : laForm.assignee2;
    if (!assignee1) return toast.error("At least one assignee is required");
    setLaSaving(true);
    try {
      const match_values = laForm.rule_type === "region"
        ? { states: laForm.states.split(",").map((s) => s.trim()).filter(Boolean) }
        : { categories: [{
            name: laForm.category_name.trim() || laForm.name.trim(),
            keywords: laForm.keywords.split(",").map((k) => k.trim()).filter(Boolean),
          }] };
      const payload = {
        name: laForm.name.trim(),
        rule_type: laForm.rule_type,
        match_values,
        assignee_emails: [assignee1, assignee2].filter(Boolean),
        strategy: laForm.strategy,
        active: laForm.active,
      };
      if (laEditingRule) {
        await api.patch(`/lead-assignment/rules/${laEditingRule.id}`, payload);
      } else {
        await api.post("/lead-assignment/rules", payload);
      }
      setLaDialogOpen(false);
      await loadLeadAssignment();
      toast.success(laEditingRule ? "Rule updated" : "Rule created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setLaSaving(false); }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/lead-assignment/rules/${rule.id}`);
      await loadLeadAssignment();
      toast.success("Rule deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const toggleRuleActive = async (rule) => {
    try {
      await api.patch(`/lead-assignment/rules/${rule.id}`, { active: !rule.active });
      await loadLeadAssignment();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const moveRule = async (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= laRules.length) return;
    const reordered = [...laRules];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];
    setLaRules(reordered);
    try {
      await api.post("/lead-assignment/rules/reorder", { rule_ids: reordered.map((r) => r.id) });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed");
      await loadLeadAssignment();
    }
  };

  const saveFallbackOwner = async () => {
    if (laFallbackBusy) return;
    setLaFallbackBusy(true);
    try {
      await api.put("/lead-assignment/settings", { fallback_owner_email: laFallback === "__none__" ? "" : laFallback });
      toast.success("Fallback owner saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setLaFallbackBusy(false); }
  };

  const runBackfillAssignment = async () => {
    if (laBackfillBusy) return;
    if (!window.confirm("Re-run assignment for all unassigned/needs-manual leads? Manually-assigned leads are never touched.")) return;
    setLaBackfillBusy(true);
    try {
      const { data } = await api.post("/lead-assignment/backfill");
      toast.success(`Re-ran assignment — ${data.touched} lead(s) updated`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Backfill failed"); }
    finally { setLaBackfillBusy(false); }
  };

  const testLeadAssignment = async () => {
    if (laTesting) return;
    setLaTesting(true);
    try {
      const { data } = await api.post("/lead-assignment/test", laTestInput);
      setLaTestResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test failed"); }
    finally { setLaTesting(false); }
  };

  const generateWebhookKey = () => {
    const bytes = new Uint8Array(24);
    window.crypto.getRandomValues(bytes);
    const key = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
    setImWebhookKeyDraft(key);
    setImWebhookUrlDraft(`${webhookBaseFromUrl(imWebhookUrlDraft)}?key=${key}`);
  };

  const saveWebhookKey = async () => {
    if (imWebhookBusy || !imCfg) return;
    if (!isValidWebhookKey(imWebhookKeyDraft)) return toast.error(webhookKeyError);
    setImWebhookBusy(true);
    try {
      // Include the Pull-API fields as-is — PUT replaces the whole settings
      // doc, and both cards share it, so this must not reset the other card.
      const payload = {
        api_base_url: imCfg.api_base_url,
        auto_sync: imCfg.auto_sync,
        sync_interval_minutes: imCfg.sync_interval_minutes,
        default_assignee: imCfg.default_assignee,
        backfill_days: imCfg.backfill_days,
        backfill_creates_tasks: imCfg.backfill_creates_tasks,
        webhook_key: imWebhookKeyDraft,
      };
      const { data } = await api.put("/settings/indiamart", payload);
      setImCfg(data);
      seedWebhookDrafts(data); // refetch-and-re-render both fields from the server's own response
      toast.success("Webhook secret saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setImWebhookBusy(false); }
  };

  const copyWebhookUrl = async () => {
    if (!imWebhookUrlDraft) return;
    try {
      await navigator.clipboard.writeText(imWebhookUrlDraft);
      toast.success(webhookDirty ? "Copied — click Save first, or IndiaMART will still hit the old secret" : "Webhook URL copied");
    } catch { toast.error("Copy failed — select and copy the URL manually"); }
  };

  const sendTestLead = async () => {
    if (imSendingTest) return;
    setImSendingTest(true);
    try {
      const { data } = await api.post("/indiamart/webhook/test-lead");
      toast.success(data.message || "Test lead sent");
      await loadIndiamart();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to send test lead"); }
    finally { setImSendingTest(false); }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto mt-12 bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="settings-403">
        <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto" />
        <h2 className="text-xl font-bold mt-3 text-slate-800" style={{ fontFamily: "Manrope" }}>Admins only</h2>
        <p className="text-sm text-slate-500 mt-1">Settings are restricted to administrators.</p>
      </div>
    );
  }

  if (!cfg) return <div className="text-slate-500 text-sm">Loading...</div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/tally", cfg);
      toast.success("Tally settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Administration</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Settings
        </h1>
        <p className="text-sm text-slate-500 mt-1">Configure integrations & system preferences.</p>
      </div>

      <AutoProcurementSendSettings />

      <DispatchNotificationSettings />

      <ReleaseAllStockSettings />

      {/* Company Profile — drives GST intra/inter-state detection */}
      {company && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="company-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Company Profile</h3>
              <p className="text-xs text-slate-500">The <strong>State</strong> is critical — it decides whether each Transfer Order is treated as Intra-State (CGST + SGST) or Inter-State (IGST).</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Company Name</Label><Input value={company.name || ""} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="company-name-input" /></div>
            <div><Label>GSTIN</Label><Input value={company.gstin || ""} onChange={(e) => setCompany({ ...company, gstin: e.target.value })} placeholder="29AAAAA0000A1Z5" data-testid="company-gstin-input" /></div>
            <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} /></div>
            <div><Label>City</Label><Input value={company.city || ""} onChange={(e) => setCompany({ ...company, city: e.target.value })} /></div>
            <div><Label>State *</Label><Input value={company.state || ""} onChange={(e) => setCompany({ ...company, state: e.target.value })} placeholder="Karnataka" data-testid="company-state-input" /></div>
            <div><Label>Country</Label><Input value={company.country || "India"} onChange={(e) => setCompany({ ...company, country: e.target.value })} /></div>
            <div><Label>PIN</Label><Input value={company.pin || ""} onChange={(e) => setCompany({ ...company, pin: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={company.phone || ""} onChange={(e) => setCompany({ ...company, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={company.email || ""} onChange={(e) => setCompany({ ...company, email: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Company Logo</Label>
              <div className="mt-1 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be ≤ 2 MB"); e.target.value = ""; return; }
                      const fd = new FormData(); fd.append("file", file);
                      try {
                        setBusy(true);
                        const { data } = await api.post("/settings/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        setCompany({ ...company, logo_storage_path: data.logo_storage_path, logo_content_type: data.logo_content_type });
                        setLogoPreviewNonce(Date.now());
                        toast.success("Logo uploaded");
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
                      finally { setBusy(false); e.target.value = ""; }
                    }}
                    data-testid="company-logo-upload-input"
                    className="text-sm"
                  />
                  {company.logo_storage_path && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          setBusy(true);
                          await api.delete("/settings/company/logo");
                          setCompany({ ...company, logo_storage_path: "", logo_content_type: "" });
                          setLogoPreviewNonce(Date.now());
                          toast.success("Logo removed");
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
                        finally { setBusy(false); }
                      }}
                      data-testid="company-logo-remove-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove logo
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-slate-500">PNG · JPG · WEBP · GIF · SVG · max 2 MB. Appears on Quotation, Transfer Order and Delivery Challan PDFs.</p>
                {company.logo_storage_path && logoPreviewSrc && (
                  <img
                    src={logoPreviewSrc}
                    alt="uploaded logo"
                    className="h-16 object-contain border border-slate-200 rounded bg-white p-1 self-start"
                    data-testid="company-logo-preview"
                  />
                )}
              </div>
              <div className="mt-3">
                <Label>Or use an External Logo URL</Label>
                <Input value={company.logo_url || ""} onChange={(e) => setCompany({ ...company, logo_url: e.target.value })} placeholder="https://your-cdn.com/canares-logo.png" data-testid="company-logo-url-input" />
                <p className="text-[11px] text-slate-500 mt-1">Used only if no logo is uploaded above.</p>
                {!company.logo_storage_path && company.logo_url && (
                  <img src={company.logo_url} alt="logo preview" className="mt-2 h-14 object-contain border border-slate-200 rounded bg-white p-1" />
                )}
              </div>
            </div>
          </div>
          {/* Phase 34.1 — Default Terms & Bank Details (appear on every PDF/print) */}
          <div className="mt-6 border-t border-slate-200 pt-4">
            <h4 className="text-base font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Default Terms &amp; Conditions</h4>
            <p className="text-xs text-slate-500 mt-0.5">Shown at the bottom of every Quotation, Transfer Order and Delivery Challan PDF/print — used unless the document links its own Terms record.</p>
            <Textarea
              rows={5}
              value={company.default_terms || ""}
              onChange={(e) => setCompany({ ...company, default_terms: e.target.value })}
              placeholder="1. Payment: 30 days net.&#10;2. Delivery: Ex-works.&#10;3. Warranty: 12 months.&#10;4. Jurisdiction: …"
              className="mt-2"
              data-testid="company-default-terms-input"
            />
          </div>

          <div className="mt-6 border-t border-slate-200 pt-4">
            <h4 className="text-base font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Default Bank Details</h4>
            <p className="text-xs text-slate-500 mt-0.5">Shown alongside Terms &amp; Conditions on every PDF/print.</p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Bank Name</Label><Input value={company.default_bank_name || ""} onChange={(e) => setCompany({ ...company, default_bank_name: e.target.value })} placeholder="HDFC Bank" data-testid="company-bank-name-input" /></div>
              <div><Label>Account Holder Name</Label><Input value={company.default_bank_account_name || ""} onChange={(e) => setCompany({ ...company, default_bank_account_name: e.target.value })} placeholder="Canares Automation Pvt Ltd" data-testid="company-bank-account-name-input" /></div>
              <div><Label>Account Number</Label><Input value={company.default_bank_account_number || ""} onChange={(e) => setCompany({ ...company, default_bank_account_number: e.target.value })} placeholder="50100123456789" data-testid="company-bank-account-input" /></div>
              <div><Label>IFSC</Label><Input value={company.default_bank_ifsc || ""} onChange={(e) => setCompany({ ...company, default_bank_ifsc: e.target.value })} placeholder="HDFC0001234" data-testid="company-bank-ifsc-input" /></div>
              <div className="sm:col-span-2"><Label>Branch</Label><Input value={company.default_bank_branch || ""} onChange={(e) => setCompany({ ...company, default_bank_branch: e.target.value })} placeholder="Andheri West, Mumbai" data-testid="company-bank-branch-input" /></div>
              <div className="sm:col-span-2"><Label>UPI ID <span className="text-[10px] font-normal text-slate-500">(optional)</span></Label><Input value={company.default_bank_upi_id || ""} onChange={(e) => setCompany({ ...company, default_bank_upi_id: e.target.value })} placeholder="canares@hdfcbank" data-testid="company-bank-upi-input" /></div>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={saveCompany} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="save-company-btn">Save Company Profile</Button>
          </div>
        </div>
      )}

      {/* GST Rate Master */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="gst-rates-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-rose-50 flex items-center justify-center">
            <Percent className="w-5 h-5 text-rose-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>GST Rate Master</h3>
            <p className="text-xs text-slate-500">Reusable GST slabs. Each rate auto-splits into CGST+SGST for intra-state or IGST for inter-state when applied to a Transfer Order line.</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {gstRates.length === 0 && <div className="text-xs text-slate-500 italic">No GST rates yet. Common slabs: 5%, 12%, 18%, 28%.</div>}
          {gstRates.map((g) => (
            <div key={g.id} className={`border rounded-md p-3 flex items-center justify-between gap-3 ${g.is_default ? "border-amber-300 bg-amber-50" : "border-slate-200"}`} data-testid={`gst-row-${g.id}`}>
              <div className="flex-1 flex items-center gap-4">
                <div>
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    {g.name}
                    {g.is_default && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 flex items-center gap-1" data-testid={`gst-default-badge-${g.id}`}>
                        <Star className="w-3 h-3" /> Default
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mono">
                    Total {g.gst_pct}% · Intra: CGST {(g.gst_pct/2).toFixed(2)}% + SGST {(g.gst_pct/2).toFixed(2)}% · Inter: IGST {g.gst_pct}%
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${g.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{g.status}</span>
              </div>
              {!g.is_default && (
                <Button variant="outline" size="sm" onClick={() => setDefaultGst(g.id)} data-testid={`gst-set-default-${g.id}`} className="text-xs">
                  <StarOff className="w-3.5 h-3.5 mr-1" /> Set default
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => rmGst(g.id)} data-testid={`gst-del-${g.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-4 grid grid-cols-3 gap-2 items-end">
          <div className="col-span-2"><Label>Tax Name</Label><Input placeholder="GST 18% Aluminium Pipes" value={newGst.name} onChange={(e) => setNewGst({ ...newGst, name: e.target.value })} data-testid="new-gst-name" /></div>
          <div><Label>Total GST %</Label><Input type="number" value={newGst.gst_pct} onChange={(e) => setNewGst({ ...newGst, gst_pct: parseFloat(e.target.value || 0) })} data-testid="new-gst-pct" /></div>
          <div className="col-span-3 flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="new-gst-default-toggle">
              <input type="checkbox" checked={!!newGst.is_default} onChange={(e) => setNewGst({ ...newGst, is_default: e.target.checked })} />
              Set as default GST rate (auto-applied to new Transfer Order &amp; Quotation lines)
            </label>
            <Button onClick={addGst} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="add-gst-btn"><Plus className="w-4 h-4 mr-1" />Add GST Rate</Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-2xl" data-testid="invoice-numbering-section">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
            <FileText className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Invoice Numbering</h3>
            <p className="text-xs text-slate-500">Tax Invoice numbers come from Tally. Each invoice goes to Tally as a Sales voucher without a number; Tally numbers it in its own series and the ERP pulls that number in as the invoice number. Keep the Sales voucher type's numbering on Automatic in Tally.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
            <Database className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Tally Integration</h3>
            <p className="text-xs text-slate-500">Push Sales Order (Transfer Orders), Tax Invoices and Delivery Challans to TallyPrime. XML download always available regardless.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
            <div>
              <div className="text-sm font-medium text-slate-800">Enable direct push to Tally</div>
              <div className="text-xs text-slate-500">When off, only XML download works.</div>
            </div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="tally-enabled-toggle" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
            <div>
              <div className="text-sm font-medium text-slate-800">Send Tax Invoices to Tally when issued</div>
              <div className="text-xs text-slate-500">Each issued Tax Invoice becomes a Sales invoice in Tally automatically. Sales Order go to Tally only as order vouchers, so every sale is booked once — at invoicing.</div>
            </div>
            <Switch checked={cfg.auto_push_invoices ?? true} onCheckedChange={(v) => setCfg({ ...cfg, auto_push_invoices: v })} data-testid="tally-auto-push-invoices-toggle" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
            <div>
              <div className="text-sm font-medium text-slate-800">Create missing ledgers &amp; stock items in Tally</div>
              <div className="text-xs text-slate-500">Before a voucher, create its customer ledger (under Sundry Debtors), stock items and units if Tally doesn't have them yet. Items and customers already linked by Tally Data use Tally's own names.</div>
            </div>
            <Switch checked={cfg.create_missing_masters ?? true} onCheckedChange={(v) => setCfg({ ...cfg, create_missing_masters: v })} data-testid="tally-create-masters-toggle" />
          </div>
          <div>
            <Label>Connection</Label>
            <Select value={cfg.mode || "direct"} onValueChange={(v) => setCfg({ ...cfg, mode: v })}>
              <SelectTrigger data-testid="tally-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Tally Bridge Agent (Tally on an office PC)</SelectItem>
                <SelectItem value="direct">Direct (this server can reach Tally's URL)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-1">
              {(cfg.mode || "direct") === "agent"
                ? "Vouchers are queued here; the agent on the Tally PC picks them up over the internet and posts them to Tally. Nothing needs to be opened on the office network."
                : "This server POSTs each voucher straight to the URL below."}
            </div>
          </div>
          {(cfg.mode || "direct") === "direct" && (
            <div>
              <Label>Tally HTTP Gateway URL</Label>
              <Input value={cfg.url} onChange={(e) => setCfg({ ...cfg, url: e.target.value })} placeholder="http://192.168.1.50:9000" data-testid="tally-url-input" />
              <div className="text-[11px] text-slate-500 mt-1">Default Tally Prime port is 9000. Reachable from this server.</div>
            </div>
          )}
          {(cfg.mode || "direct") === "agent" && (
            <div className="border border-slate-200 rounded-md p-3 space-y-3" data-testid="tally-agent-panel">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  {agentStatus?.online ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Agent online{agentStatus.hostname ? ` · ${agentStatus.hostname}` : ""}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-500"><XCircle className="w-4 h-4" /> Agent offline{agentStatus?.last_seen_at ? ` · last seen ${new Date(agentStatus.last_seen_at).toLocaleString()}` : ""}</span>
                  )}
                  {agentStatus?.online && (
                    <div className={`text-xs mt-1 ${agentStatus.tally_reachable ? "text-emerald-700" : "text-red-600"}`}>
                      {agentStatus.tally_reachable ? "TallyPrime is reachable from the agent" : (agentStatus.tally_error || "TallyPrime is not reachable from the agent")}
                    </div>
                  )}
                  <div className="text-xs text-slate-500 mt-1">
                    Queue: {agentStatus?.queue?.pending ?? 0} waiting · {agentStatus?.queue?.in_progress ?? 0} sending · {agentStatus?.queue?.failed ?? 0} failed
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={loadTallyAgent} title="Refresh"><RefreshCw className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={generateAgentToken} data-testid="tally-agent-token-btn">
                    {agentStatus?.token_configured ? "Rotate agent token" : "Generate agent token"}
                  </Button>
                </div>
              </div>
              {agentToken && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs space-y-1">
                  <div className="font-medium text-amber-800">Copy this token into tally_agent.ini on the Tally PC now — it won't be shown again.</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 break-all bg-white border border-amber-200 rounded px-2 py-1" data-testid="tally-agent-token">{agentToken}</code>
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(agentToken); toast.success("Copied"); }}><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
              {agentQueue.length > 0 && (
                <div className="max-h-56 overflow-y-auto border border-slate-100 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left px-2 py-1">Document</th><th className="text-left px-2 py-1">Status</th><th className="text-left px-2 py-1">When</th><th /></tr></thead>
                    <tbody>
                      {agentQueue.map((j) => (
                        <tr key={j.id} className="border-t border-slate-100 align-top">
                          <td className="px-2 py-1">{j.doc_code}</td>
                          <td className="px-2 py-1">
                            <span className={j.status === "done" ? "text-emerald-700" : j.status === "failed" ? "text-red-600" : j.status === "replaced" ? "text-slate-400" : "text-amber-700"}>{j.status}</span>
                            {j.error && <div className="text-[11px] text-red-600">{j.error}</div>}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">{new Date(j.updated_at).toLocaleString()}</td>
                          <td className="px-2 py-1 text-right">
                            {j.status === "failed" && <Button variant="ghost" size="sm" onClick={() => retryAgentJob(j.id)}>Retry</Button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tally Company Name</Label>
              <Input value={cfg.company_name} onChange={(e) => setCfg({ ...cfg, company_name: e.target.value })} data-testid="tally-company-input" />
            </div>
            <div>
              <Label>DC Voucher Type</Label>
              <Input value={cfg.voucher_type} onChange={(e) => setCfg({ ...cfg, voucher_type: e.target.value })} placeholder="Delivery Note" />
            </div>
            <div>
              <Label>Sales Order Voucher Type</Label>
              <Input value={cfg.so_voucher_type || ""} onChange={(e) => setCfg({ ...cfg, so_voucher_type: e.target.value })} placeholder="Sales Order" data-testid="tally-so-voucher-input" />
            </div>
            <div>
              <Label>Invoice Voucher Type</Label>
              <Input value={cfg.invoice_voucher_type || ""} onChange={(e) => setCfg({ ...cfg, invoice_voucher_type: e.target.value })} placeholder="Sales" data-testid="tally-invoice-voucher-input" />
            </div>
            <div>
              <Label>Invoice Godown</Label>
              <Input value={cfg.godown_name || ""} onChange={(e) => setCfg({ ...cfg, godown_name: e.target.value })} placeholder="Main Location" data-testid="tally-godown-input" />
            </div>
          </div>
          <div className="border border-slate-200 rounded-md p-3 space-y-3" data-testid="tally-ledger-names">
            <div>
              <div className="text-sm font-medium text-slate-800">Ledgers a Tax Invoice posts to</div>
              <div className="text-xs text-slate-500">
                Must be exactly as named in Tally.{" "}
                {tallyLedgers.length
                  ? `Pick from the ${tallyLedgers.length} sales / GST / round-off ledgers pulled from Tally, or type a name.`
                  : "Turn on Tally Data → Pull from Tally (Ledger) to get a list of your Tally ledgers here."}
              </div>
            </div>
            <datalist id="tally-account-ledgers">
              {tallyLedgers.map((l) => <option key={l.name} value={l.name}>{l.group}</option>)}
            </datalist>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["sales_ledger", "Sales ledger", "Sales GST"],
                ["round_off_ledger", "Round-off ledger", "Round Off"],
                ["cgst_ledger", "CGST ledger", "Output CGST"],
                ["sgst_ledger", "SGST ledger", "Output SGST"],
                ["igst_ledger", "IGST ledger", "Output IGST"],
              ].map(([key, label, ph]) => {
                const v = cfg[key] || "";
                const known = !tallyLedgers.length || !v || tallyLedgers.some((l) => l.name === v);
                return (
                  <div key={key}>
                    <Label>{label}</Label>
                    <Input list="tally-account-ledgers" value={v} placeholder={ph} onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} data-testid={`tally-${key}`} />
                    {!known && <div className="text-[11px] text-amber-700 mt-1">Not found in Tally's ledger list — check the spelling.</div>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="pt-2">
            <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="save-tally-btn">Save</Button>
          </div>
        </div>
      </div>

      {/* IndiaMART Integration — Pull API (CRM key), replaces the old push webhook card */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="indiamart-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-indigo-50 flex items-center justify-center">
            <Radio className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>IndiaMART Integration</h3>
            <p className="text-xs text-slate-500">Pulls new inquiries from your IndiaMART CRM (Pull API) on a schedule — no seller-panel webhook needed.</p>
          </div>
        </div>

        {!imCfg ? (
          <div className="text-xs text-slate-500 italic">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>IndiaMART CRM Key (glusr_crm_key)</Label>
              <PasswordInput
                value={imCrmKeyInput}
                onChange={(e) => setImCrmKeyInput(e.target.value)}
                placeholder={imCfg.has_crm_key ? imCfg.crm_key_masked : "Paste your CRM key"}
                data-testid="indiamart-crm-key-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                {imCfg.has_crm_key ? `Currently saved: ${imCfg.crm_key_masked}. Leave blank to keep it unchanged.` : "Not configured yet."} Encrypted at rest — never shown in full again.
              </div>
            </div>

            <div>
              <Label>API Base URL</Label>
              <Input
                value={imCfg.api_base_url}
                onChange={(e) => setImCfg({ ...imCfg, api_base_url: e.target.value })}
                className="mono text-xs"
                data-testid="indiamart-api-base-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
                <div>
                  <div className="text-sm font-medium text-slate-800">Auto-sync</div>
                  <div className="text-xs text-slate-500">Pull automatically on the interval below.</div>
                </div>
                <Switch checked={imCfg.auto_sync} onCheckedChange={(v) => setImCfg({ ...imCfg, auto_sync: v })} data-testid="indiamart-autosync-toggle" />
              </div>
              <div>
                <Label>Sync interval</Label>
                <Select value={String(imCfg.sync_interval_minutes)} onValueChange={(v) => setImCfg({ ...imCfg, sync_interval_minutes: parseInt(v, 10) })}>
                  <SelectTrigger data-testid="indiamart-interval-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 30].map((m) => <SelectItem key={m} value={String(m)}>Every {m} min</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Default assignee for new tasks</Label>
                <Select value={imCfg.default_assignee || "__none__"} onValueChange={(v) => setImCfg({ ...imCfg, default_assignee: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="indiamart-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {imUsers.map((u) => <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Initial backfill — fetch last N days</Label>
                <Input
                  type="number"
                  min={1}
                  value={imCfg.backfill_days}
                  onChange={(e) => setImCfg({ ...imCfg, backfill_days: parseInt(e.target.value || "1", 10) })}
                  data-testid="indiamart-backfill-days-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
              <div>
                <div className="text-sm font-medium text-slate-800">Create tasks for backfilled leads</div>
                <div className="text-xs text-slate-500">
                  Off by default so the initial historical backfill doesn't flood Sales Report → Tasks with one entry per
                  old inquiry. Leads are still imported either way — this only controls whether a Task is raised for them.
                  Auto/manual syncs of new leads always create tasks regardless of this setting.
                </div>
              </div>
              <Switch
                checked={!!imCfg.backfill_creates_tasks}
                onCheckedChange={(v) => setImCfg({ ...imCfg, backfill_creates_tasks: v })}
                data-testid="indiamart-backfill-tasks-toggle"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Button onClick={saveIndiamart} disabled={imBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="indiamart-save-btn">Save</Button>
              <Button onClick={testIndiamart} disabled={imTesting} variant="outline" data-testid="indiamart-test-btn">
                <PlugZap className="w-4 h-4 mr-1.5" /> {imTesting ? "Testing…" : "Test Connection"}
              </Button>
              <Button onClick={syncIndiamartNow} disabled={imSyncing} variant="outline" data-testid="indiamart-sync-now-btn">
                <RefreshCw className={`w-4 h-4 mr-1.5 ${imSyncing ? "animate-spin" : ""}`} /> {imSyncing ? "Syncing…" : "Sync Now"}
              </Button>
            </div>

            {/* Status */}
            <div className="border-t border-slate-100 pt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs" data-testid="indiamart-status">
              <div>
                <div className="text-slate-500 uppercase tracking-wide text-[10px]">Last Sync</div>
                <div className="font-medium text-slate-800 mt-0.5">{imCfg.status?.last_sync_at ? new Date(imCfg.status.last_sync_at).toLocaleString() : "Never"}</div>
              </div>
              <div>
                <div className="text-slate-500 uppercase tracking-wide text-[10px]">
                  Result{imCfg.status?.last_attempt_kind && (
                    <span className="normal-case text-slate-400"> ({imCfg.status.last_attempt_kind === "test" ? "Test Connection" : "Sync Now"})</span>
                  )}
                </div>
                <div className="font-medium mt-0.5 flex items-center gap-1">
                  {imCfg.status?.last_sync_status === "success" && <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Success</>}
                  {imCfg.status?.last_sync_status === "error" && <span className="flex items-center gap-1 text-red-600"><XCircle className="w-3.5 h-3.5" /> {imCfg.status.last_sync_message || "Error"}</span>}
                  {!imCfg.status?.last_sync_status && "—"}
                </div>
              </div>
              <div>
                <div className="text-slate-500 uppercase tracking-wide text-[10px]">Leads fetched (last sync)</div>
                <div className="font-medium text-slate-800 mt-0.5">{imCfg.status?.last_sync_fetched_count ?? 0}</div>
              </div>
              <div>
                <div className="text-slate-500 uppercase tracking-wide text-[10px]">Total leads stored</div>
                <div className="font-medium text-slate-800 mt-0.5">{imCfg.status?.total_leads_stored ?? 0}</div>
              </div>
              <div>
                <div className="text-slate-500 uppercase tracking-wide text-[10px]">Next scheduled sync</div>
                <div className="font-medium text-slate-800 mt-0.5 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  {imCfg.auto_sync && imCfg.status?.next_scheduled_sync ? new Date(imCfg.status.next_scheduled_sync).toLocaleString() : "Auto-sync off"}
                </div>
              </div>
              {imCfg.status?.backfill_in_progress && (
                <div>
                  <div className="text-slate-500 uppercase tracking-wide text-[10px]">Initial backfill</div>
                  <div className="font-medium text-amber-700 mt-0.5">In progress — {imCfg.status.backfill_progress}</div>
                </div>
              )}
            </div>

            {/* Outbound IP — for whitelisting on IndiaMART's side */}
            <div className="border-t border-slate-100 pt-4 flex items-start justify-between gap-3 flex-wrap" data-testid="indiamart-outbound-ip">
              <div>
                <div className="text-slate-500 uppercase tracking-wide text-[10px] flex items-center gap-1">
                  <Globe className="w-3 h-3" /> Server Outbound IP
                </div>
                <div className="font-medium text-slate-800 mt-0.5 mono text-xs" data-testid="indiamart-outbound-ip-value">
                  {imCfg.outbound_ip || "Not checked yet"}
                  {imCfg.outbound_ip_checked_at && (
                    <span className="text-slate-400 font-normal ml-2">as of {new Date(imCfg.outbound_ip_checked_at).toLocaleString()}</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 max-w-md">
                  If IndiaMART is blocking the Pull API by IP, whitelist this address in their seller panel. If detection
                  fails here, run <code className="mono">curl -s https://api.ipify.org</code> on the server itself, or
                  check your hosting/firewall provider's dashboard for its outbound/NAT IP.
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={detectOutboundIp} disabled={imDetectingIp} data-testid="indiamart-detect-ip-btn">
                {imDetectingIp ? "Detecting…" : "Detect Outbound IP"}
              </Button>
            </div>

            {/* Recent sync logs */}
            {imSyncLogs.length > 0 && (
              <div className="border-t border-slate-100 pt-4">
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Recent Sync Log</div>
                <div className="space-y-1.5 max-h-72 overflow-y-auto scroll-thin" data-testid="indiamart-sync-logs">
                  {imSyncLogs.map((l) => {
                    const hasDiagnostics = l.status !== "success" && (l.request_url_masked || l.response_body);
                    const expanded = imExpandedLogId === l.id;
                    return (
                      <div key={l.id} className="text-xs border border-slate-100 rounded px-2.5 py-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {l.status === "success" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                            <span className="text-slate-600 shrink-0">{new Date(l.started_at).toLocaleString()}</span>
                            <span className="text-[10px] uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded shrink-0">{l.trigger}</span>
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="mono text-slate-500 truncate">
                              {l.status === "success" ? `${l.new_count} new / ${l.duplicate_count} dup / ${l.fetched_count} fetched` : l.error_message}
                            </div>
                            {hasDiagnostics && (
                              <button
                                type="button"
                                className="text-[10px] text-blue-600 hover:underline shrink-0"
                                onClick={() => setImExpandedLogId(expanded ? null : l.id)}
                                data-testid={`indiamart-sync-log-details-toggle-${l.id}`}
                              >
                                {expanded ? "Hide details" : "Details"}
                              </button>
                            )}
                          </div>
                        </div>
                        {expanded && hasDiagnostics && (
                          <div
                            className="mt-2 pt-2 border-t border-slate-100 space-y-1 mono text-[11px] text-slate-600 break-all whitespace-pre-wrap"
                            data-testid={`indiamart-sync-log-details-${l.id}`}
                          >
                            {l.request_url_masked && <div><span className="text-slate-400">Request:</span> {l.request_url_masked}</div>}
                            {l.request_headers && Object.keys(l.request_headers).length > 0 && (
                              <div><span className="text-slate-400">Request headers:</span> {JSON.stringify(l.request_headers)}</div>
                            )}
                            {l.response_status != null && (
                              <div><span className="text-slate-400">Response status:</span> {l.response_status}</div>
                            )}
                            {l.response_body && (
                              <div><span className="text-slate-400">Response body:</span> {l.response_body}</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* IndiaMART Webhook — Push API, complements the Pull-API card above */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="indiamart-webhook-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-indigo-50 flex items-center justify-center">
            <Webhook className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>IndiaMART Webhook</h3>
            <p className="text-xs text-slate-500">Paste this URL into your IndiaMART seller panel so new inquiries POST here instantly, instead of waiting for the next scheduled pull.</p>
          </div>
        </div>

        {!imCfg ? (
          <div className="text-xs text-slate-500 italic">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="mb-0">Webhook URL &amp; Secret</Label>
              {webhookDirty && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="indiamart-webhook-unsaved-badge">
                  Unsaved changes
                </span>
              )}
            </div>

            <div>
              <Label className="text-xs text-slate-500 font-normal">Webhook URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={imWebhookUrlDraft}
                  onChange={onWebhookUrlChange}
                  className="mono text-xs"
                  data-testid="indiamart-webhook-url"
                />
                <Button variant="outline" size="icon" onClick={copyWebhookUrl} disabled={!imWebhookUrlDraft} data-testid="indiamart-webhook-copy-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Editing this parses out the <code className="mono">key</code> and updates Secret Key below; the two always stay in sync.
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500 font-normal">Secret Key</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1">
                  <PasswordInput
                    value={imWebhookKeyDraft}
                    onChange={onWebhookKeyChange}
                    placeholder="Generate or paste a secret (min. 16 characters)"
                    data-testid="indiamart-webhook-key-input"
                  />
                </div>
                <Button variant="outline" onClick={generateWebhookKey} data-testid="indiamart-webhook-generate-btn">
                  <Shuffle className="w-4 h-4 mr-1.5" /> Generate
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Letters, digits, <code className="mono">-</code> and <code className="mono">_</code> only — pasting a full URL here auto-extracts the key. Encrypted at rest; {imCfg.has_webhook_key ? `currently saved as ${imCfg.webhook_key_masked}.` : "not configured yet — POSTs are rejected with 401 until saved."}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Button onClick={saveWebhookKey} disabled={imWebhookBusy || !webhookDirty} className="bg-blue-600 hover:bg-blue-700" data-testid="indiamart-webhook-save-btn">
                {imWebhookBusy ? "Saving…" : "Save"}
              </Button>
              <Button variant="outline" onClick={resetWebhookDraft} disabled={!webhookDirty} data-testid="indiamart-webhook-reset-btn">
                Reset
              </Button>
              <Button onClick={sendTestLead} disabled={imSendingTest || !imCfg.has_webhook_key} variant="outline" data-testid="indiamart-send-test-lead-btn">
                <Send className="w-4 h-4 mr-1.5" /> {imSendingTest ? "Sending…" : "Send Test Lead"}
              </Button>
            </div>

            <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-3 text-xs" data-testid="indiamart-webhook-status">
              <div>
                <div className="text-slate-500 uppercase tracking-wide text-[10px]">Last Received</div>
                <div className="font-medium text-slate-800 mt-0.5">{imCfg.webhook_last_received_at ? new Date(imCfg.webhook_last_received_at).toLocaleString() : "Never"}</div>
              </div>
              <div>
                <div className="text-slate-500 uppercase tracking-wide text-[10px]">Total Leads Received</div>
                <div className="font-medium text-slate-800 mt-0.5">{imCfg.status?.total_leads_stored ?? 0}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lead Assignment Rules — auto-assign IndiaMART leads (Push, Pull and
          any future import path — they all converge on the same lead-creation
          pipeline in backend/indiamart.py) by product category and region. */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-4xl" data-testid="lead-assignment-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-indigo-50 flex items-center justify-center">
            <Users className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Lead Assignment Rules</h3>
            <p className="text-xs text-slate-500">Auto-assign new IndiaMART leads (and their Sales Report → Tasks task) to a sales rep by product category, then region. Rules run in order — first match wins.</p>
          </div>
        </div>

        <div className="space-y-1.5 mb-4" data-testid="lead-assignment-rules-list">
          {laRules.length === 0 && <div className="text-xs text-slate-500 italic py-2">No rules yet — click "Add Rule" below.</div>}
          {laRules.map((rule, idx) => (
            <div key={rule.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 text-xs" data-testid={`lead-assignment-rule-${rule.id}`}>
              <div className="flex flex-col shrink-0">
                <button type="button" className="text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={idx === 0} onClick={() => moveRule(idx, -1)} data-testid={`lead-assignment-rule-up-${rule.id}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button type="button" className="text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={idx === laRules.length - 1} onClick={() => moveRule(idx, 1)} data-testid={`lead-assignment-rule-down-${rule.id}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">{rule.rule_type}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{rule.name}</div>
                <div className="text-slate-500 truncate">
                  {rule.rule_type === "region"
                    ? (rule.match_values?.states || []).join(", ") || "—"
                    : (rule.match_values?.categories || []).map((c) => c.name).join(", ") || "—"}
                  {" · "}
                  {(rule.assignee_emails || []).map(userLabel).join(rule.strategy === "round_robin" ? " ⇄ " : " + ") || "Unassigned"}
                  {rule.strategy === "round_robin" && <span className="ml-1 text-slate-400">(round-robin)</span>}
                </div>
              </div>
              <Switch checked={rule.active} onCheckedChange={() => toggleRuleActive(rule)} data-testid={`lead-assignment-rule-active-${rule.id}`} />
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditRuleDialog(rule)} data-testid={`lead-assignment-rule-edit-${rule.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => deleteRule(rule)} data-testid={`lead-assignment-rule-delete-${rule.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap pb-4 border-b border-slate-100">
          <Button onClick={openNewRuleDialog} variant="outline" data-testid="lead-assignment-add-rule-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Rule
          </Button>
          <Button onClick={runBackfillAssignment} disabled={laBackfillBusy} variant="outline" data-testid="lead-assignment-backfill-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${laBackfillBusy ? "animate-spin" : ""}`} /> Re-run assignment for unassigned leads
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-4 border-b border-slate-100">
          <div>
            <Label>Fallback owner</Label>
            <div className="text-[11px] text-slate-500 mb-1">Used when no rule matches (unmapped state, unrecognised location, or outside India) — the task is flagged "Needs manual assignment".</div>
            <Select value={laFallback || "__none__"} onValueChange={setLaFallback}>
              <SelectTrigger data-testid="lead-assignment-fallback-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {imUsers.map((u) => <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={saveFallbackOwner} disabled={laFallbackBusy} data-testid="lead-assignment-fallback-save-btn">Save Fallback Owner</Button>
          </div>
        </div>

        <div className="pt-4">
          <div className="flex items-center gap-1.5 mb-2">
            <FlaskConical className="w-3.5 h-3.5 text-slate-500" />
            <Label className="mb-0">Test a lead</Label>
          </div>
          <div className="text-[11px] text-slate-500 mb-2">Enter a state/city/product and see which rule would match — nothing is saved.</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Input placeholder="State" value={laTestInput.state} onChange={(e) => setLaTestInput({ ...laTestInput, state: e.target.value })} data-testid="lead-assignment-test-state" />
            <Input placeholder="City" value={laTestInput.city} onChange={(e) => setLaTestInput({ ...laTestInput, city: e.target.value })} data-testid="lead-assignment-test-city" />
            <Input placeholder="Pincode" value={laTestInput.pincode} onChange={(e) => setLaTestInput({ ...laTestInput, pincode: e.target.value })} data-testid="lead-assignment-test-pincode" />
            <Input placeholder="Country ISO (IN)" value={laTestInput.country_iso} onChange={(e) => setLaTestInput({ ...laTestInput, country_iso: e.target.value })} data-testid="lead-assignment-test-country" />
            <Input placeholder="Product text" value={laTestInput.product_text} onChange={(e) => setLaTestInput({ ...laTestInput, product_text: e.target.value })} data-testid="lead-assignment-test-product" />
          </div>
          <Button onClick={testLeadAssignment} disabled={laTesting} variant="outline" className="mt-2" data-testid="lead-assignment-test-btn">
            {laTesting ? "Testing…" : "Test"}
          </Button>
          {laTestResult && (
            <div className="mt-3 text-xs border border-slate-200 rounded-md p-3" data-testid="lead-assignment-test-result">
              <div className="flex items-center gap-1.5">
                {laTestResult.needs_manual
                  ? <span className="flex items-center gap-1 text-amber-700"><XCircle className="w-3.5 h-3.5" /> Needs manual assignment</span>
                  : <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Matched</span>}
              </div>
              <div className="mt-1 text-slate-700">Assigned to: <span className="font-medium">{laTestResult.assigned_to ? userLabel(laTestResult.assigned_to) : "Unassigned"}</span></div>
              <div className="text-slate-500 mono">{laTestResult.reason}</div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={laDialogOpen} onOpenChange={setLaDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="lead-assignment-rule-dialog">
          <DialogHeader>
            <DialogTitle>{laEditingRule ? "Edit Rule" : "Add Rule"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Rule name</Label>
              <Input value={laForm.name} onChange={(e) => setLaForm({ ...laForm, name: e.target.value })} data-testid="lead-assignment-rule-name-input" />
            </div>
            <div>
              <Label>Rule type</Label>
              <Select value={laForm.rule_type} onValueChange={(v) => setLaForm({ ...laForm, rule_type: v })} disabled={!!laEditingRule}>
                <SelectTrigger data-testid="lead-assignment-rule-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="region">Region (by state)</SelectItem>
                  <SelectItem value="product">Product (keyword match, any region)</SelectItem>
                </SelectContent>
              </Select>
              {laEditingRule && <div className="text-[11px] text-slate-500 mt-1">Rule type can't change after creation — delete and re-add if you need a different type.</div>}
            </div>
            {laForm.rule_type === "region" ? (
              <div>
                <Label>States (comma-separated)</Label>
                <Textarea rows={2} placeholder="Karnataka, Tamil Nadu" value={laForm.states} onChange={(e) => setLaForm({ ...laForm, states: e.target.value })} data-testid="lead-assignment-rule-states-input" />
              </div>
            ) : (
              <>
                <div>
                  <Label>Category name</Label>
                  <Input placeholder="Profile Accessories" value={laForm.category_name} onChange={(e) => setLaForm({ ...laForm, category_name: e.target.value })} data-testid="lead-assignment-rule-category-input" />
                </div>
                <div>
                  <Label>Keywords (comma-separated, matched case-insensitively)</Label>
                  <Textarea rows={2} placeholder="bracket, hinge, corner connector" value={laForm.keywords} onChange={(e) => setLaForm({ ...laForm, keywords: e.target.value })} data-testid="lead-assignment-rule-keywords-input" />
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assignee</Label>
                <Select value={laForm.assignee1} onValueChange={(v) => setLaForm({ ...laForm, assignee1: v })}>
                  <SelectTrigger data-testid="lead-assignment-rule-assignee1-select"><SelectValue placeholder="Choose a user" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Choose a user</SelectItem>
                    {imUsers.map((u) => <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>2nd assignee (optional)</Label>
                <Select value={laForm.assignee2} onValueChange={(v) => setLaForm({ ...laForm, assignee2: v })}>
                  <SelectTrigger data-testid="lead-assignment-rule-assignee2-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {imUsers.map((u) => <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {laForm.assignee2 !== "__none__" && (
              <div>
                <Label>With two assignees</Label>
                <Select value={laForm.strategy} onValueChange={(v) => setLaForm({ ...laForm, strategy: v })}>
                  <SelectTrigger data-testid="lead-assignment-rule-strategy-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="round_robin">Round-robin (alternate leads evenly)</SelectItem>
                    <SelectItem value="both">Both — first is primary, second is collaborator</SelectItem>
                    <SelectItem value="single">Single — always the first assignee (2nd is a backup, unused)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
              <div className="text-sm font-medium text-slate-800">Active</div>
              <Switch checked={laForm.active} onCheckedChange={(v) => setLaForm({ ...laForm, active: v })} data-testid="lead-assignment-rule-active-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLaDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveRule} disabled={laSaving} data-testid="lead-assignment-rule-save-btn">{laSaving ? "Saving…" : "Save Rule"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terms & Conditions */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="terms-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center">
            <FileText className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Terms & Conditions Templates</h3>
            <p className="text-xs text-slate-500">Shared library used by Raise Transfer Order, Quotations and Purchase Orders — only <strong>Active</strong> templates appear in those pickers.</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {terms.length === 0 && <div className="text-xs text-slate-500 italic">No templates yet.</div>}
          {terms.map((t) => {
            const isActive = t.is_active !== false; // missing field == active (pre-toggle templates)
            return (
            <div key={t.id} className="border border-slate-200 rounded-md p-3" data-testid={`term-row-${t.id}`}>
              {editingTerm?.id === t.id ? (
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs text-slate-500 font-normal">Title</Label>
                    <Input
                      value={editingTerm.name}
                      onChange={(e) => setEditingTerm({ ...editingTerm, name: e.target.value })}
                      placeholder="Title"
                      className="mt-1"
                      data-testid={`term-edit-name-${t.id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 font-normal">Body text</Label>
                    <Textarea
                      rows={5}
                      value={editingTerm.body}
                      onChange={(e) => setEditingTerm({ ...editingTerm, body: e.target.value })}
                      placeholder="Full Terms & Conditions text…"
                      className="mt-1"
                      data-testid={`term-edit-body-${t.id}`}
                    />
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <Switch
                          checked={editingTerm.is_default}
                          onCheckedChange={(v) => setEditingTerm({ ...editingTerm, is_default: v })}
                        /> Default template
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <Switch
                          checked={editingTerm.is_active}
                          onCheckedChange={(v) => setEditingTerm({ ...editingTerm, is_active: v })}
                          data-testid={`term-edit-active-${t.id}`}
                        /> Active
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={cancelEditTerm} data-testid={`term-edit-cancel-${t.id}`}>Cancel</Button>
                      <Button size="sm" onClick={saveEditTerm} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid={`term-edit-save-${t.id}`}>Save</Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                      {t.is_default ? <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> : <StarOff className="w-3.5 h-3.5 text-slate-300" />}
                      {t.name}
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${isActive ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap line-clamp-3">{t.body}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEditTerm(t)} data-testid={`term-edit-${t.id}`} className="text-slate-600 hover:text-slate-900">
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => rmTerm(t.id)} data-testid={`term-del-${t.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>

        <div className="border-t border-slate-100 pt-4 space-y-2">
          <div>
            <Label className="text-xs text-slate-500 font-normal">Title</Label>
            <Input placeholder="e.g., Standard Sales Terms" value={newTerm.name} onChange={(e) => setNewTerm({ ...newTerm, name: e.target.value })} className="mt-1" data-testid="new-term-name" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 font-normal">Body text</Label>
            <Textarea placeholder="1. Payment 50% advance, balance against PI...&#10;2. Delivery within 4 weeks..." rows={4} value={newTerm.body} onChange={(e) => setNewTerm({ ...newTerm, body: e.target.value })} className="mt-1" data-testid="new-term-body" />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Switch checked={newTerm.is_default} onCheckedChange={(v) => setNewTerm({ ...newTerm, is_default: v })} /> Default template
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Switch checked={newTerm.is_active} onCheckedChange={(v) => setNewTerm({ ...newTerm, is_active: v })} data-testid="new-term-active" /> Active
              </label>
            </div>
            <Button onClick={addTerm} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="add-term-btn"><Plus className="w-4 h-4 mr-1" />Add New T&amp;C</Button>
          </div>
        </div>
      </div>

      {/* Bank Accounts */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-3xl" data-testid="banks-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-amber-50 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Bank Accounts</h3>
            <p className="text-xs text-slate-500">Attachable to Transfer Orders + Delivery Challans for payment instructions.</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {banks.length === 0 && <div className="text-xs text-slate-500 italic">No bank accounts yet.</div>}
          {banks.map((b) => (
            <div key={b.id} className="border border-slate-200 rounded-md p-3 flex items-start justify-between gap-3" data-testid={`bank-row-${b.id}`}>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                  {b.is_default ? <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> : <StarOff className="w-3.5 h-3.5 text-slate-300" />}
                  {b.name}
                </div>
                <div className="text-xs text-slate-600 mt-0.5 mono">
                  A/c: {b.account_number} · IFSC: {b.ifsc}{b.branch ? ` · ${b.branch}` : ""}
                </div>
                <div className="text-xs text-slate-500">{b.account_name}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => rmBank(b.id)} data-testid={`bank-del-${b.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-2">
          <Input placeholder="Bank Name" value={newBank.name} onChange={(e) => setNewBank({ ...newBank, name: e.target.value })} data-testid="new-bank-name" />
          <Input placeholder="Account Holder" value={newBank.account_name} onChange={(e) => setNewBank({ ...newBank, account_name: e.target.value })} />
          <Input placeholder="Account Number" value={newBank.account_number} onChange={(e) => setNewBank({ ...newBank, account_number: e.target.value })} />
          <Input placeholder="IFSC Code" value={newBank.ifsc} onChange={(e) => setNewBank({ ...newBank, ifsc: e.target.value })} />
          <Input placeholder="Branch (optional)" value={newBank.branch} onChange={(e) => setNewBank({ ...newBank, branch: e.target.value })} className="col-span-2" />
          <div className="col-span-2 flex items-center justify-between mt-1">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Switch checked={newBank.is_default} onCheckedChange={(v) => setNewBank({ ...newBank, is_default: v })} /> Default bank
            </label>
            <Button onClick={addBank} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="add-bank-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
